import { createClient } from '@supabase/supabase-js';

function trimQuotes(val: string): string {
  if (!val) return val;
  let v = val.trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1);
  }
  return v;
}

// Hardcoded fallback for external hosting (Netlify, etc.) where .env vars may not be present at build time.
// The sb_publishable_ key is a public anon key designed to be exposed in browser bundles.
const FALLBACK_SUPABASE_URL = 'https://nwg0s0zm0a3dmec1fr2o.helloreaddy.com';
const FALLBACK_SUPABASE_ANON_KEY = 'sb_publishable_DUoMdM247vgBr7X9YpEYDbyin2O2L37H';

const rawSupabaseUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) || '';
const rawSupabaseAnonKey = (import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string) || '';

const supabaseUrl = (trimQuotes(rawSupabaseUrl) || FALLBACK_SUPABASE_URL).replace(/\/$/, '');
const supabaseAnonKey = trimQuotes(rawSupabaseAnonKey) || FALLBACK_SUPABASE_ANON_KEY;

let supabaseInstance: ReturnType<typeof createClient> | null = null;

if (supabaseUrl && supabaseAnonKey) {
  try {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
      },
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Supabase client creation failed:', err);
  }
} else {
  // eslint-disable-next-line no-console
  console.warn('Supabase credentials missing — auth and blog features will be unavailable. URL:', !!supabaseUrl, 'Key:', !!supabaseAnonKey);
}

// Provide a minimal no-op fallback so imports don't throw when env vars are missing
export const supabase = supabaseInstance ?? (createClient('https://placeholder.supabase.co', 'placeholder') as ReturnType<typeof createClient>);

// Low-level REST request that returns a normalized result.
async function rawRestFetch(
  endpoint: string,
  headers: Record<string, string>
): Promise<{ ok: boolean; status: number; data: any[] | null; error: string }> {
  try {
    const res = await fetch(endpoint, { headers });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return {
        ok: false,
        status: res.status,
        data: null,
        error: text
          ? `Backend HTTP ${res.status}: ${text.slice(0, 200)}`
          : `Backend HTTP ${res.status} (${res.statusText})`,
      };
    }
    const json = await res.json();
    const arr = Array.isArray(json) ? json : [json];
    return { ok: true, status: res.status, data: arr, error: '' };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, status: 0, data: null, error: `Network error: ${msg}` };
  }
}

// Direct REST fetch that tries several authentication strategies in order,
// working around backend gateway quirks with the publishable key.
export async function directFetch(
  table: string,
  params: Record<string, string>
): Promise<{ data: any[] | null; error: string | null }> {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { data: null, error: 'Backend credentials missing' };
  }
  const search = new URLSearchParams(params).toString();
  const endpoint = `${supabaseUrl}/rest/v1/${table}?${search}`;

  const strategies: Array<{ name: string; url: string; headers: Record<string, string> }> = [
    {
      name: 'apikey-header',
      url: endpoint,
      headers: { Accept: 'application/json', apikey: supabaseAnonKey },
    },
    {
      name: 'apikey-auth',
      url: endpoint,
      headers: {
        Accept: 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
    },
    {
      name: 'apikey-query',
      url: `${endpoint}${search ? '&' : '?'}apikey=${encodeURIComponent(supabaseAnonKey)}`,
      headers: { Accept: 'application/json' },
    },
  ];

  let lastError = '';
  for (const strategy of strategies) {
    const result = await rawRestFetch(strategy.url, strategy.headers);
    if (result.ok && result.data) {
      return { data: result.data, error: null };
    }
    lastError = result.error || `${strategy.name} failed`;
  }
  return { data: null, error: lastError || 'All backend fetch strategies failed' };
}

export async function directFetchSingle(
  table: string,
  params: Record<string, string>
): Promise<{ data: any | null; error: string | null }> {
  const { data, error } = await directFetch(table, params);
  if (error || !data) return { data: null, error };
  const arr = Array.isArray(data) ? data : [data];
  return { data: arr[0] || null, error: null };
}